import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { PhaseJourneyTimeline } from '@/components/PhaseJourneyTimeline';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhaseReassessmentPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/phase/reassessment-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const nextButtonLabel = currentQuestion === questions.length - 1 ? 'Complete' : 'Next';

  const handleNext = () => {
    if (selectedOption === null) {
      toast.error('Please select an answer');
      return;
    }

    const newAnswers = [
      ...answers,
      {
        question_id: questions[currentQuestion].id,
        question: questions[currentQuestion].text,
        score: selectedOption,
      },
    ];

    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submitReassessment(newAnswers);
    }
  };

  const submitReassessment = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/phase/reassess`,
        { answers: finalAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      await refreshUser();
    } catch (error) {
      toast.error('Failed to submit reassessment');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <div className="animate-pulse text-stone-600">Loading assessment...</div>
      </div>
    );
  }

  if (result) {
    const phaseColors = {
      1: 'bg-phase-stabilize-bg border-phase-stabilize',
      2: 'bg-phase-understand-bg border-phase-understand',
      3: 'bg-phase-reclaim-bg border-phase-reclaim',
      4: 'bg-phase-renew-bg border-phase-renew',
    };

    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl text-center" data-testid="reassessment-result-screen">
          {result.advanced ? (
            <div className="mb-8 animate-pulse">
              <Sparkles className="w-20 h-20 text-amber-500 mx-auto mb-4" />
              <h1
                className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Congratulations!
              </h1>
              <p className="text-lg text-stone-600 mb-8">
                You've progressed to your next phase
              </p>
            </div>
          ) : (
            <h1
              className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Assessment Complete
            </h1>
          )}

          <div
            className={`${phaseColors[result.phase]} rounded-3xl p-12 border-2 mb-8`}
          >
            <div className="text-sm font-medium text-stone-600 mb-2">
              {result.advanced ? 'YOUR NEW PHASE' : 'YOUR CURRENT PHASE'}
            </div>
            <h2
              className="text-3xl md:text-4xl font-medium mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Phase {result.phase}: {result.phase_name}
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed">
              {result.description}
            </p>
          </div>

          {result.advanced && (
            <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 mb-8">
              <p className="text-stone-700 leading-relaxed">
                Your progress shows you're ready for the next stage of healing. You'll now receive
                prompts and guidance tailored to this new phase.
              </p>
            </div>
          )}

          <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 mb-8">
            <p className="text-sm font-medium text-stone-500 tracking-[0.15em] mb-6 text-center">YOUR JOURNEY SO FAR</p>
            <PhaseJourneyTimeline variant="light" />
          </div>

          <Button
            onClick={() => navigate('/sanctuary')}
            className="rounded-full px-12 py-6 text-lg bg-stone-800 text-stone-50 hover:bg-stone-700 shadow-lg"
          >
            Return to Sanctuary
          </Button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        <Button
          onClick={() => navigate('/sanctuary')}
          variant="ghost"
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Sanctuary
        </Button>

        <div className="mb-8">
          <h1
            className="text-3xl font-medium mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Check Your Progress
          </h1>
          <p className="text-stone-600 mb-6">
            Answer these 5 questions to see if you're ready to advance to your next phase.
          </p>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-stone-500">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <span className="text-sm text-stone-500">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 md:p-10">
          <h2
            className="text-2xl md:text-3xl font-medium mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="reassessment-question-text"
          >
            {questions[currentQuestion]?.text}
          </h2>

          <RadioGroup
            key={currentQuestion}
            value={selectedOption !== null ? selectedOption.toString() : ''} 
            onValueChange={(val) => setSelectedOption(parseInt(val))}
            data-testid="reassessment-options"
          >
            <div className="space-y-4">
              {questions[currentQuestion]?.options.map((option, idx) => (
                <Label
                  key={idx}
                  htmlFor={`option-${idx}`}
                  className="flex items-center space-x-3 p-4 rounded-xl border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer"
                  data-testid={`reassessment-option-${idx}`}
                >
                  <RadioGroupItem value={option.score.toString()} id={`option-${idx}`} />
                  <span className="flex-1 text-base">{option.label}</span>
                </Label>
              ))}
            </div>
          </RadioGroup>

          <div className="flex justify-between mt-8">
            <Button
              onClick={() => {
                if (currentQuestion > 0) {
                  setCurrentQuestion(currentQuestion - 1);
                  setAnswers(answers.slice(0, -1));
                  setSelectedOption(null);
                }
              }}
              disabled={currentQuestion === 0}
              variant="ghost"
              className="rounded-full"
              data-testid="reassessment-back-button"
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={submitting}
              className="rounded-full px-8 bg-stone-800 text-stone-50 hover:bg-stone-700"
              data-testid="reassessment-next-button"
            >
              {submitting ? 'Submitting...' : nextButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
